import { useCallback, useMemo, useState, useEffect } from "react";

import { Button, Input, InputGroup } from "twyne-new.ui";

import DefaultDataTool from "@Components/DataTool";
import { Select, Async as SelectAsync } from "components/pure/form-fields/Select";
import { genderOptions, stateOptions, getCountryOptions, monthOptions, dayOptions, yearOptions } from "components/rules/options";
import allocationStore from "modules/offer/stores/allocation"
import offerStore from "modules/offer/stores/offer"
import publisherStore from "modules/offer/stores/publisher"

import { getRandomEmail } from "../utils";

import { SeedProvider, useSeed } from "./SeedContext";

export default function ParamsTool({ extraFields, metaFields = [], ...props }) {
  const customFieldsDefinitions = useMemo(() => {
    const definitions = {}
    extraFields.forEach(({ key, name: label, type, options }) => {
      const props = type === "date" ? { mask: "MM/DD/YYYY" } : {}
      definitions[key] = { label, type, options, ...props }
    })
    return definitions
  }, [extraFields])

  const metaFieldsDefinitions = useMemo(() => {
    const definitions = {}
    metaFields.forEach(({ key }) => {
      definitions[key] = { label: key, type: "input" }
    })
    return definitions
  }, [metaFields])

  const definitions = useMemo(() => ({
    incommingTraffic: {
      label: "Incomming Traffic",
      fields: {
        aid: { label: "Allocation", Component: CustomAllocation },
        pid: { label: "Publisher", Component: CustomPublisher },
        __testab_offer: { label: "Offer / Target Post", Component: CustomOffer },
        sid1: { label: "SID 1", type: "input" },
        sid2: { label: "SID 2", type: "input" },
        sid3: { label: "SID 3", type: "input" },
      },
    },
    consumer: {
      label: "Consumer",
      fields: {
        first_name: { label: "First Name", type: "input" },
        last_name: { label: "Last Name", type: "input" },
        email: { label: "Email", Component: Email },
        address1: { label: "Address 1", type: "input" },
        address2: { label: "Address 2", type: "input" },
        city: { label: "City", type: "input" },
        state: { label: "State", type: "select", options: stateOptions },
        zip: { label: "Zip", type: "input" },
        phone: { label: "Phone", type: "input" },
        country: { label: "Country", type: "select", options: getCountryOptions() },
        dob_month: { label: "Date of birth (Month)", type: "select", options: monthOptions },
        dob_day: { label: "Date of birth (Day)", type: "select", options: dayOptions() },
        dob_year: { label: "Date of birth (Year)", type: "select", options: yearOptions() },
        gender: { label: "Gender", type: "select", options: genderOptions },
        optout: { label: "Opt-out", type: "boolean" },
        mobile: { label: "Mobile", type: "boolean" },
      },
    },
    customFields: {
      label: "Custom Fields",
      fields: customFieldsDefinitions,
    },
    metaFields: {
      label: "Meta Fields",
      fields: metaFieldsDefinitions,
    },
  }), [customFieldsDefinitions, metaFieldsDefinitions])

  return <SeedProvider>
    <DefaultDataTool definitions={definitions} {...props} />
  </SeedProvider>
}

function CustomAllocation({ value, onChange }) {
  const { traffic, setTraffic } = useSeed()

  const loadOptions = useCallback(async () => {
    const { data } = await allocationStore.all()
    const options = data.map(({ id, description, publisher_id, offers }) => ({
      label: `${id} - ${description}`,
      value: id,
      publisher_id,
      offers: (offers || []).map(offer => offer.id),
    }))

    if (value && !traffic) {
      const allocation = options.find(option => option.value === value)
      setTraffic(allocation)
    }

    return { options }
  }, [value, traffic, setTraffic])

  const handleChange = useCallback(option => {
    onChange(option?.value)
    setTraffic(option)
  }, [onChange, setTraffic])

  return <SelectAsync
    required
    name="allocation-option"
    loadOptions={loadOptions}
    value={value}
    onChange={handleChange}
    clearable={false}
  />
}

function CustomPublisher({ value, onChange }) {
  const { traffic } = useSeed()

  const loadOptions = useCallback(async () => {
    const { data } = await publisherStore.all()
    const options = data.map(({ name, id }) => ({
      label: `${id} - ${name}`,
      value: id,
    }))
    return { options }
  }, [])

  useEffect(() => {
    const publisherId = traffic?.publisher_id || null
    if (publisherId !== value) {
      onChange(traffic?.publisher_id)
    }
  }, [traffic?.publisher_id, value, onChange])

  return <SelectAsync
    required
    name="publisher-option"
    loadOptions={loadOptions}
    value={traffic?.publisher_id}
    clearable={false}
    disabled
  />
}

function CustomOffer({ value, onChange }) {
  const { traffic } = useSeed()
  const [options, setOptions] = useState([])
  const [filtered, setFiltered] = useState([])

  const parseOffer = useCallback(offerId => {
    const value = `oid:${offerId.toString()}`
    return encodeURIComponent(btoa(value))
  }, [])

  const loadOptions = useCallback(async () => {
    const { data } = await offerStore.all()

    const options = data.map(({ name, id, lander }) => ({
      label: `${id} - ${name} / ${lander.id} - ${lander.title}`,
      value: parseOffer(id),
    }))
    setOptions(options)
  }, [parseOffer])

  const filterOptions = useCallback((options, traffic) => {
    const offers = (traffic?.offers || []).map(id => parseOffer(id))
    setFiltered(options.filter(offer => offers.includes(offer.value)))
  }, [parseOffer])

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  useEffect(() => {
    filterOptions(options, traffic)
  }, [options, traffic, filterOptions])

  useEffect(() => {
    if (filtered.length === 1) {
      const [offer] = filtered
      if (offer && offer.value !== value) {
        onChange(offer.value)
      }
    }
  }, [filtered, onChange, value])

  const handleChange = useCallback(option => {
    onChange(option?.value)
  }, [onChange])

  return <Select
    required
    name="offer-option"
    options={filtered}
    value={value}
    onChange={handleChange}
    clearable={false}
    disabled={filtered.length === 1}
  />
}

function Email({ name, type, value, onChange, ...props }) {
  const handleGenerate = useCallback(() => {
    onChange(getRandomEmail())
  }, [onChange])

  const handleChange = useCallback(event => {
    onChange(event.target?.value)
  }, [onChange])

  return <InputGroup className='w-full'>
    <Input name="email" value={value} onChange={handleChange} {...props} className='w-full' />
    <Button color="accent" onClick={handleGenerate}>
      Generate
    </Button>
  </InputGroup>
}
